import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'
import CreateOrEditMediaLinkModal from '@modals/CreateOrEditMediaLinkModal'
import CreateOrEditUserModal from '@modals/CreateOrEditUserModal'
import DeleteAgentModal from '@modals/DeleteAgentModal'
import ManageEbuAssignedAgentsModal from '@modals/ManageEbuAssignedAgentsModal'
import ManageNotificationOptionsModal from '@modals/ManageNotificationOptionsModal'
import ManageUserPriorityCampaignsModal from '@modals/ManageUserPriorityCampaignsModal'
import ManageUserIntegrationModal from '@modals/ManageUserIntegrationModal'
import ManageUserPermissionsModal from '@modals/ManageUserPermissionsModal'

import {
  ImageModel, MediaLinkModel, PriorityCampaignModel, UserModel,
} from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditImageModal: false,
  showCreateOrEditMediaLinkModal: false,
  showCreateOrEditUserModal: false,
  showDeleteAgentModal: false,
  showManageEbuAssignedAgentsModal: false,
  showManageNotificationOptionsModal: false,
  showManageUserPriorityCampaignsModal: false,
  showManageUserIntegrationModal: false,
  showManageUserPermissionsModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      deleteImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setCoverImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditMediaLinkModal: {
      closeModal: () => setState({ showCreateOrEditMediaLinkModal: false }),
      createMediaLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateMediaLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditUserModal: {
      closeModal: () => setState({ showCreateOrEditUserModal: false }),
      updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    DeleteAgentModal: {
      closeModal: () => setState({ showDeleteAgentModal: false }),
      deleteAgent: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageEbuAssignedAgentsModal: {
      closeModal: () => setState({ showManageEbuAssignedAgentsModal: false }),
      updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageNotificationOptionsModal: {
      closeModal: () => setState({ showManageNotificationOptionsModal: false }),
      updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageUserPriorityCampaignsModal: {
      closeModal: () => setState({ showManageUserPriorityCampaignsModal: false }),
      createPriorityCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageUserIntegrationModal: {
      closeModal: () => setState({ showManageUserIntegrationModal: false }),
    },
    ManageUserPermissionsModal: {
      closeModal: () => setState({ showManageUserPermissionsModal: false }),
      updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditImageModal,
    showCreateOrEditMediaLinkModal,
    showCreateOrEditUserModal,
    showDeleteAgentModal,
    showManageEbuAssignedAgentsModal,
    showManageNotificationOptionsModal,
    showManageUserPriorityCampaignsModal,
    showManageUserIntegrationModal,
    showManageUserPermissionsModal,
  } = state

  const { userId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(
    () => ({
      callbacks: {
        showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
          setModalData('CreateOrEditImageModal', payload)
          setState({ showCreateOrEditImageModal: true })
        },
        showCreateOrEditMediaLinkModal: (payload: { mediaLink: MediaLinkModel }) => {
          setModalData('CreateOrEditMediaLinkModal', payload)
          setState({ showCreateOrEditMediaLinkModal: true })
        },
        showDeleteAgentModal: (payload: { entity: UserModel }) => {
          setModalData('DeleteAgentModal', payload)
          setState({ showDeleteAgentModal: true })
        },
        showManageEbuAssignedAgentsModal: (payload: { user: UserModel }) => {
          setModalData('ManageEbuAssignedAgentsModal', payload)
          setState({ showManageEbuAssignedAgentsModal: true })
        },
        showManageNotificationOptionsModal: (payload: { user: UserModel }) => {
          setModalData('ManageNotificationOptionsModal', payload)
          setState({ showManageNotificationOptionsModal: true })
        },
        showManageUserPriorityCampaignsModal: (payload: { priorityCampaign?: Partial<PriorityCampaignModel> }) => {
          setModalData('ManageUserPriorityCampaignsModal', payload)
          setState({ showManageUserPriorityCampaignsModal: true })
        },
        showManageUserIntegrationModal: (payload: { user: UserModel }) => {
          setModalData('ManageUserIntegrationModal', payload)
          setState({ showManageUserIntegrationModal: true })
        },
        showManageUserPermissionsModal: (payload: { entity: UserModel }) => {
          setModalData('ManageUserPermissionsModal', payload)
          setState({ showManageUserPermissionsModal: true })
        },
        updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      },
    }),
    [],
  )

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent userId={userId} />

        <CreateOrEditImageModal
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />

        <CreateOrEditMediaLinkModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditMediaLinkModal', setState)}
          showModal={showCreateOrEditMediaLinkModal}
          permittedLinkTypes={['facebook', 'instagram', 'reviews', 'twitter', 'website']}
        />

        <CreateOrEditUserModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditUserModal', setState)}
          showModal={showCreateOrEditUserModal}
        />

        <DeleteAgentModal
          // @ts-expect-error TS2322
          callbacks={callbacks('DeleteAgentModal', setState)}
          showModal={showDeleteAgentModal}
        />

        <ManageEbuAssignedAgentsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageEbuAssignedAgentsModal', setState)}
          showModal={showManageEbuAssignedAgentsModal}
        />

        <ManageNotificationOptionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageNotificationOptionsModal', setState)}
          showModal={showManageNotificationOptionsModal}
        />

        <ManageUserPriorityCampaignsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageUserPriorityCampaignsModal', setState)}
          showModal={showManageUserPriorityCampaignsModal}
        />

        <ManageUserIntegrationModal
          callbacks={callbacks('ManageUserIntegrationModal', setState)}
          showModal={showManageUserIntegrationModal}
        />

        <ManageUserPermissionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageUserPermissionsModal', setState)}
          showModal={showManageUserPermissionsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
